import { useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
// Version RECADRÉE du logo (sans le remplissage du PNG de marque) : c'est la même image
// que celle embarquée dans l'imageset iOS et dans splash_logo Android, donc l'afficher à
// 220 px donne exactement la taille du natif. Utiliser flowcut-logo.png ici afficherait un
// logo plus petit (son contenu n'occupe que 84 % de la largeur du fichier) → saut visible.
import splashLogo from "@/assets/splash-logo.png";

/**
 * Couche d'écran de lancement animée.
 *
 * Pourquoi elle existe : sous Capacitor, l'écran de lancement natif (storyboard iOS,
 * drawable Android) est une IMAGE STATIQUE — il ne peut pas être animé. L'animation doit
 * donc vivre dans le web, avec l'écran natif maintenu en amont pour éviter tout flash blanc.
 *
 * Enchaînement (aucun saut, aucun double logo) :
 *   1. natif  : logo centré, 220 pt, fond blanc              (statique)
 *   2. web    : cette couche peint EXACTEMENT la même image  (invisible, sous le natif)
 *   3. hide() : l'écran natif est retiré → rien ne bouge à l'écran
 *   4. web    : l'animation démarre (zoom du logo + dégradés)
 *   5. web    : fondu de sortie, la couche est démontée      (l'app est déjà dessous)
 *
 * La correspondance à l'étape 2 tient à deux choses : le logo fait 220 pt des deux côtés
 * (le storyboard est en contentMode="center", donc taille naturelle, pas de mise à
 * l'échelle selon l'appareil), et le fond est blanc opaque des deux côtés.
 *
 * L'app est montée SOUS cette couche dès le départ : l'animation ne retarde jamais l'accès.
 */

const LOGO_PT = 220; // doit rester égal à la largeur de l'imageset iOS / du splash_logo Android

type Phase = "attente" | "animation" | "sortie";

interface SplashOverlayProps {
  /** Passe à true quand l'app est prête à être montrée. */
  ready: boolean;
}

export const SplashOverlay = ({ ready }: SplashOverlayProps) => {
  const [monte, setMonte] = useState(true);
  const [phase, setPhase] = useState<Phase>("attente");
  const relaisFait = useRef(false);

  // Étape 3 — prendre le relais du natif dès que cette couche a été peinte.
  useEffect(() => {
    if (relaisFait.current) return;
    relaisFait.current = true;

    let annule = false;
    // Deux rAF : garantit que le navigateur a réellement peint la couche avant
    // qu'on retire l'écran natif (un seul rAF s'exécute avant la peinture).
    requestAnimationFrame(() => {
      requestAnimationFrame(async () => {
        if (annule) return;
        if (Capacitor.isNativePlatform()) {
          try {
            const { SplashScreen } = await import("@capacitor/splash-screen");
            await SplashScreen.hide();
          } catch {
            // Plugin absent ou indisponible : la couche web suffit, on continue.
          }
        }
        if (!annule) setPhase("animation");
      });
    });

    return () => {
      annule = true;
    };
  }, []);

  // Étape 5 — sortir dès que l'app est prête, sans jamais la faire attendre.
  useEffect(() => {
    if (phase === "attente" || !ready) return;
    const t = window.setTimeout(() => setPhase("sortie"), 260);
    return () => window.clearTimeout(t);
  }, [phase, ready]);

  useEffect(() => {
    if (phase !== "sortie") return;
    const t = window.setTimeout(() => setMonte(false), 420);
    return () => window.clearTimeout(t);
  }, [phase]);

  if (!monte) return null;

  return (
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "#ffffff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        pointerEvents: "none",
        opacity: phase === "sortie" ? 0 : 1,
        transition: "opacity 400ms ease-out",
      }}
    >
      {/* Dégradés : absents de l'image native, ils n'apparaissent qu'en fondu APRÈS
          le relais — donc jamais de différence visible au moment de la bascule. */}
      <div className={`fc-splash-blobs${phase === "attente" ? "" : " fc-splash-blobs--on"}`}>
        <span className="fc-splash-blob fc-splash-blob--1" />
        <span className="fc-splash-blob fc-splash-blob--2" />
        <span className="fc-splash-blob fc-splash-blob--3" />
        <span className="fc-splash-blob fc-splash-blob--4" />
      </div>

      <img
        src={splashLogo}
        alt=""
        className={`fc-splash-logo${phase === "attente" ? "" : " fc-splash-logo--on"}`}
        style={{ width: LOGO_PT }}
      />
    </div>
  );
};

export default SplashOverlay;
